// src/components/TemplatesTab.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";

interface SavedTemplate {
  id: string;
  title: string;
  content: string;
}

interface TemplatesTabProps {
  templates: SavedTemplate[];
  currentInputValue: string;
  canSaveToServer: boolean;
  onApplyTemplate: (template: string) => void;
  onSaveTemplate: (title: string, content: string) => void;
  onDeleteTemplate: (templateId: string) => void;
}

export function TemplatesTab({
  templates,
  currentInputValue,
  canSaveToServer,
  onApplyTemplate,
  onSaveTemplate,
  onDeleteTemplate
}: TemplatesTabProps) {
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateTitle, setNewTemplateTitle] = useState('');

  const handleSaveTemplate = () => {
    if (currentInputValue.trim() && newTemplateTitle.trim()) {
      onSaveTemplate(newTemplateTitle.trim(), currentInputValue.trim());
      setNewTemplateTitle('');
      setShowSaveTemplate(false);
    }
  };

  const toggleTemplateInput = () => {
    setShowSaveTemplate(!showSaveTemplate);
    if (!showSaveTemplate) {
      // Focus the template input field after opening
      setTimeout(() => {
        const titleInput = document.getElementById('template-title-input');
        if (titleInput) {
          titleInput.focus();
        }
      }, 100);
    }
  };

  return (
    <>
      <div className="py-2 px-3 border-b flex justify-between items-center">
        <h4 className="font-medium">Response Templates</h4>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleTemplateInput}
          className="h-7 px-2 text-xs"
          disabled={!currentInputValue.trim()}
        >
          {showSaveTemplate ? 'Cancel' : 'Save Current'}
        </Button>
      </div>
      
      {showSaveTemplate && (
        <div className="p-3 border-b">
          <input
            id="template-title-input"
            type="text"
            className="w-full p-2 text-sm border rounded mb-2"
            placeholder="Template title"
            value={newTemplateTitle}
            onChange={(e) => setNewTemplateTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveTemplate();
              }
            }}
          />
          <div className="flex justify-end">
            <Button 
              size="sm" 
              onClick={handleSaveTemplate}
              disabled={!newTemplateTitle.trim() || !currentInputValue.trim()}
            >
              Save Template
            </Button>
          </div>
        </div>
      )}
      
      <ScrollArea className="h-72">
        <div className="py-2">
          {templates.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500 text-sm">
              No templates saved yet
            </div>
          ) : (
            templates.map((template) => (
              <div 
                key={template.id} 
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b"
                onClick={() => onApplyTemplate(template.content)}
              >
                <div className="text-sm font-medium">{template.title}</div>
                <div className="text-xs text-gray-500 mt-1 truncate flex justify-between">
                  <span>
                    {template.content.substring(0, 60)}
                    {template.content.length > 60 ? '...' : ''}
                  </span>
                  {!canSaveToServer && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTemplate(template.id);
                      }}
                      className="text-gray-400 hover:text-red-500 ml-2"
                      title="Delete template"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </>
  );
}